import { CalendarDotsIcon, LockSimpleIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { startCase } from 'lodash'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { SegmentedControl, type SegmentedOption } from 'app/components/action-center/SegmentedControl'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/PageHeader'
import ExportRows from 'app/components/table/ExportRows'
import TableLayout from 'app/components/table/TableLayout'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useDocumentTitle } from 'app/hooks/useDocumentTitle'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'
import type { AppointmentsWhen } from 'lib/appointmentsPage'
import { useAppointmentsPage } from './useAppointmentsPage'

const TITLE = `Appointments`

const WHEN_OPTIONS: SegmentedOption<AppointmentsWhen>[] = [
  { id: `today`, label: `Today` },
  { id: `upcoming`, label: `Upcoming` },
  { id: `past`, label: `Past` },
  { id: `all`, label: `All` },
]

const WHEN_EMPTY: Readonly<Record<AppointmentsWhen, string>> = {
  today: `Nothing is on the books for today.`,
  upcoming: `Nothing is on the books from today on.`,
  past: `No past appointments.`,
  all: `No appointments have synced from your practice management system yet.`,
}

const Appointments = observer(function Appointments(): ReactElement {
  const page = useAppointmentsPage()
  const title = page.list ? startCase(page.list.name) : TITLE
  useDocumentTitle(title)
  return (
    <PageScroll
      variant="table"
      header={
        <PageHeader
          title={title}
          totalItems={page.data?.total}
          otherButtons={[
            ...(page.list
              ? [
                  <Button key="edit-list" variant="white" size="sm" onClick={page.list.edit}>
                    <PencilSimpleIcon className="h-4 w-4" />
                    Edit list
                  </Button>,
                ]
              : []),
            <SegmentedControl key="when" options={WHEN_OPTIONS} value={page.when} onSelect={page.setWhen} />,
            <Button key="calendar" asChild variant="white" size="sm">
              <Link to="/appointments-calendar">
                <CalendarDotsIcon className="h-4 w-4" />
                Calendar
              </Link>
            </Button>,
          ]}
        />
      }
    >
      <TableLayout
        key={page.when}
        data={page.data}
        columns={page.columns}
        filterableColumns={page.filterableColumns}
        initialSort={page.initialSort}
        loadMore={page.loadMore}
        edgePadded
        extraButtons={(columns, columnVisibility) => [
          <ExportRows
            key="export"
            loaderPropertyName="appointments"
            // Until the search is whole, the last read's total would export rows outside it.
            totalSize={page.searchReady ? (page.data?.total ?? 0) : 0}
            columns={columns}
            columnVisibility={columnVisibility}
            search={page.search}
            enrichRows={page.enrichExportRows}
          />,
        ]}
        emptyState={
          <TableEmptyState
            icon={<CalendarDotsIcon className="h-6 w-6" />}
            title="No appointments match"
            description={WHEN_EMPTY[page.when]}
          />
        }
      />
      {/* Keyed so a second patient opened from this page starts the drawer fresh. */}
      <ContactDrawer key={page.drawer.contact?.id} {...page.drawer} />
    </PageScroll>
  )
})

function LockedAppointments(): ReactElement {
  useDocumentTitle(TITLE)
  return (
    <PageScroll variant="table" header={<PageHeader title={TITLE} />}>
      <TableEmptyState
        icon={<LockSimpleIcon className="h-6 w-6" />}
        title="Appointments needs a PMS integration"
        description="Appointments come from your practice management system, and none of this workspace's locations has an active PMS integration yet."
      />
    </PageScroll>
  )
}

export function AppointmentsPage(): ReactElement | null {
  const { isIntegrated } = usePmsIntegration()
  if (isIntegrated === undefined) return null
  return isIntegrated ? <Appointments /> : <LockedAppointments />
}
